/**
 * "Set as default lights" (2026-10-27, DEC-323): the timeline performance mode loops underneath every other on each
 * PC's lights (OPBridge DEC-734). OPBridge holds ONE setting for every PC; we set and read it through the routes agreed
 * with OPBridge S340 (`POST` / `GET /lights/default`), on a bridge whose `/health.capabilities.default_lights_route`
 * says it has them. Where none does, nothing here shows (the Timelines section hides the item).
 *
 * Before setting, the timeline is pushed to its home: OPBridge builds the default from the copy pushed to the PC that
 * owns the lights, and a timeline never pushed (made on an iPad a moment ago) would otherwise be a name it cannot play.
 * For a lighting timeline that home IS the lights' owner (`bridgeSync.ts::timelinePlacement`: light steps place it
 * there when nothing else ties it to a machine).
 */
import { createSignal } from 'solid-js';
import type { Timeline } from '@opbuttons/schema';
import { bridges, getConnection, primaryBridge } from '../opbridge/store.ts';
import { pushTimelineNow } from './autoSync.ts';

export const DEFAULT_LIGHTS_CAPABILITY = 'default_lights_route';

/** A connected bridge that has the default-lights routes, the primary first. */
function defaultLightsBridge() {
  const p = primaryBridge();
  const all = [...(p ? [p] : []), ...bridges().filter((b) => b.id !== p?.id)];
  return all.find((b) => {
    const c = getConnection(b.id);
    return c?.status() === 'connected' && c.health()?.capabilities?.[DEFAULT_LIGHTS_CAPABILITY] === true;
  }) ?? null;
}

/** Whether any connected OPBridge can be told which timeline is the default lights. Reactive. */
export function canSetDefaultLights(): boolean {
  return defaultLightsBridge() !== null;
}

const [currentDefault, setCurrentDefault] = createSignal<string | null>(null);
/** The default lights timeline's id as last read (null = none, or not read yet). */
export const defaultLightsId = currentDefault;

/** Read the setting (on showing the list, and after setting it). Quiet on failure: the tick just does not show. */
export async function refreshDefaultLights(): Promise<void> {
  const b = defaultLightsBridge();
  if (!b) return;
  try { setCurrentDefault((await getConnection(b.id)!.httpClient().lightsDefault()).timelineId ?? null); } catch { /* leave it */ }
}

/** Make `t` the default lights. Returns what to tell the operator. */
export async function setDefaultLights(t: Timeline): Promise<{ ok: boolean; message: string }> {
  const b = defaultLightsBridge();
  if (!b) return { ok: false, message: 'No connected OPBridge can set the default lights yet. Update OPBridge.' };
  await pushTimelineNow(t);
  try {
    const r = await getConnection(b.id)!.httpClient().setLightsDefault(t.id);
    setCurrentDefault(r.timelineId ?? t.id);
    return { ok: true, message: `"${t.name}" is the default lights. It plays under everything while performance mode is on.` };
  } catch (err) {
    return { ok: false, message: `OPBridge did not take "${t.name}" as the default lights: ${err instanceof Error ? err.message : String(err)}` };
  }
}
